import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { classifyError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import {
  atualizarRegraForense,
  contarPrazo,
  criarRegraForense,
  definirRegraAtiva,
  fetchDiasQueNaoContam,
  fetchRegrasForenses,
  fetchSugestoesForenses,
  promoverSugestao,
  recusarSugestao,
  type ContarInput,
  type LugarFiltro,
  type RegraForenseInput,
} from '../api/calendarioForense';

const RAIZ = ['admin', 'calendario-forense'] as const;

export function useRegrasForenses(lugar: LugarFiltro, incluirInativas: boolean) {
  return useQuery({
    queryKey: [...RAIZ, 'regras', lugar, incluirInativas],
    queryFn: () => fetchRegrasForenses(lugar, incluirInativas),
  });
}

export function useDiasQueNaoContam(lugar: LugarFiltro, de: string, ate: string, enabled: boolean) {
  return useQuery({
    queryKey: [...RAIZ, 'dias', lugar, de, ate],
    queryFn: () => fetchDiasQueNaoContam(lugar, de, ate),
    enabled,
  });
}

/**
 * Toda escrita muda o calendário: a lista (qualquer filtro) e a conferência ficam velhas —
 * e, como no lançamento do advogado (`useMeusFeriados`), as contas da própria conta do
 * admin: a régua do painel, as intimações e a agenda. Os vencimentos são refeitos pelo
 * outbox depois (75 s a minutos); o que chegar depois disso a régua pega ao voltar o foco.
 */
function useInvalidarCalendario() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: RAIZ }),
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.regua }),
      queryClient.invalidateQueries({ queryKey: queryKeys.comunicacoes.all }),
      queryClient.invalidateQueries({ queryKey: ['calendario'] }),
    ]);
}

export function useSalvarRegraForense() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    networkMode: 'always',
    mutationFn: ({ id, input }: { id?: string; input: RegraForenseInput }) =>
      id ? atualizarRegraForense(id, input) : criarRegraForense(input),
    onSuccess: invalidar,
  });
}

export function useDefinirRegraAtiva() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    networkMode: 'always',
    mutationFn: ({ id, ativo }: { id: string; ativo: boolean }) => definirRegraAtiva(id, ativo),
    onSuccess: invalidar,
  });
}

export function useSugestoesForenses(situacao: 'pendente' | 'promovida' | 'recusada') {
  return useQuery({
    queryKey: [...RAIZ, 'sugestoes', situacao],
    queryFn: () => fetchSugestoesForenses(situacao),
  });
}

export function usePromoverSugestao() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    networkMode: 'always',
    mutationFn: ({ id, input }: { id: string; input: RegraForenseInput }) => promoverSugestao(id, input),
    onSuccess: invalidar,
    // 409: outro admin já resolveu — a fila relê para tirá-la.
    onError: (err) => (classifyError(err).status === 409 ? invalidar() : undefined),
  });
}

export function useRecusarSugestao() {
  const invalidar = useInvalidarCalendario();
  return useMutation({
    networkMode: 'always',
    mutationFn: ({ id, nota }: { id: string; nota: string | null }) => recusarSugestao(id, nota),
    onSuccess: invalidar,
    onError: (err) => (classifyError(err).status === 409 ? invalidar() : undefined),
  });
}

export function useContarPrazo() {
  return useMutation({
    networkMode: 'always',
    mutationFn: ({ lugar, input }: { lugar: LugarFiltro; input: ContarInput }) => contarPrazo(lugar, input),
  });
}
